import { XMarkIcon, HomeIcon } from "@heroicons/react/20/solid";
import * as OutlineIcons from "@/icons/outline";
import * as SolidIcons from "@/icons/solid";
import { useMemo } from "react";
import { connect } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import BuddyFace from "../../images/buddy-logo-full.png";
import { Actions as ADMIN_ACTIONS } from "../../store/actions/adminActions";
import { Actions as AUTHENTICATION_ACTIONS } from "../../store/actions/authenticationActions";
import { Actions as PAYROLL_ACTIONS } from "../../store/actions/payrollActions";
import { Actions as SIDEBAR_ACTIONS } from "../../store/actions/sidebarActions";
import { copyToClipboard } from "./helpers/ClipboardHelpers";

const hasPermissions = (permission) => {
	if (Array.isArray(permission)) return permission.some((p) => hasPermissions(p));
	return !["None"].includes(permission);
};

const filterPermissions = (permissions, jurisdiction) => {
	// [{ permit: settings }, { permit: permissions }, { permit: billing }]
	return permissions
		.filter((item) => typeof item.jurisdiction === "undefined" || item.jurisdiction?.includes(jurisdiction))
		.map((item) => item.permit);
};

const Sidebar = (state) => {
	const location = useLocation();
	const currentTab = location.pathname;
	const { goToZendesk, advisor, company, payrollGetPayPeriod, currentPayroll: paySchedule } = state;

	const currentPayroll = useMemo(() => {
		if (paySchedule) return paySchedule;
	}, [paySchedule]);

	const [CurrentSidebar, jurisdiction] = useMemo(() => {
		if (currentTab.startsWith("/admin")) return [AdminSidebar];
		if (currentTab.startsWith("/advisor")) return [PayrollSidebar, advisor?.jurisdiction];
		if (currentTab.startsWith("/user")) return [UserSidebar];
		return [CompanySidebar, company?.jurisdiction];
	}, [currentTab, advisor?.jurisdiction, company?.jurisdiction]);

	const navigate = useNavigate();

	const supportURL = useMemo(() => {
		let language = "mt";

		switch (jurisdiction) {
			case "MT":
				language = "mt";
				break;
			case "UK":
				language = "gb";
				break;
		}
		console.debug("Jurisdiction and language", jurisdiction, language);
		return `https://support.buddy.hr/hc/en-${language.toLowerCase()}`;
	}, [jurisdiction]);

	const navigateToZendesk = (e, path) => {
		e.preventDefault();
		e.stopPropagation();
		goToZendesk(path);
	};

	const SidebarTab = ({ link, name, icon, className, checkForActive, children, target, onClick, isActive }) => {
		let path = checkForActive || link;
		path = path ? path.split("#")[0] : "";
		const paths = path.split("/");
		const tabPaths = currentTab.split("/");

		const matchingPaths = paths.filter((p, i) => p === tabPaths[i])?.length;

		const active = useMemo(
			() => isActive || (path ? paths.length / matchingPaths <= 1 : false),
			[isActive, path, paths?.length, matchingPaths],
		);
		const Icon = active ? SolidIcons[icon] : OutlineIcons[icon];
		return (
			<li className={`${active ? "active " : ""}${className || ""}`}>
				<Link
					to={link}
					className="nav-item"
					target={target}
					onClick={(e) => {
						if (onClick) {
							e.preventDefault();
							e.stopPropagation();
							onClick();
						}
					}}
				>
					{Icon && <Icon className="svg-nav-item" />}
					<span>{name}</span>
				</Link>
				{children}
			</li>
		);
	};

	if (!CurrentSidebar) return null;

	return (
		<CurrentSidebar {...{ ...state, SidebarTab, payrollGetPayPeriod, navigate, currentPayroll }}>
			<li className="nav-main-heading">
				<span className="sidebar-mini-hide">Learn</span>
			</li>
			{jurisdiction == "MT" && (
				<li>
					<a
						href="https://www.buddy.hr/learn"
						className="nav-item"
					>
						<OutlineIcons.CheckBadgeIcon className="svg-nav-item" />
						<span>Get Certified</span>
					</a>
				</li>
			)}
			<li>
				<a
					onClick={(e) => navigateToZendesk(e, supportURL)}
					href={supportURL}
					className="nav-item"
				>
					<OutlineIcons.LifebuoyIcon className="svg-nav-item" />
					<span>Support</span>
				</a>
			</li>
			<li>
				<a
					onClick={(e) => navigateToZendesk(e, `${supportURL}/categories/360001143332-General-User-Guide`)}
					href={`${supportURL}/categories/360001143332-General-User-Guide`}
					className="nav-item"
				>
					<OutlineIcons.BookOpenIcon className="svg-nav-item" />
					<span>User Guide</span>
				</a>
			</li>
			<li>
				<a
					onClick={(e) => navigateToZendesk(e, `${supportURL}/requests`)}
					href={`${supportURL}/requests`}
					className="nav-item"
				>
					<OutlineIcons.ChatBubbleOvalLeftEllipsisIcon className="svg-nav-item" />
					<span>My Tickets</span>
				</a>
			</li>
		</CurrentSidebar>
	);
};

const AdminSidebar = ({ toggleSidebar, SidebarTab, show, openUserModal, openCertificateModal }) => {
	if (!show) return null;

	return (
		<nav
			id="sidebar"
			className="sidebar-admin"
		>
			<div
				className="slimScrollDiv"
				style={{ position: "relative" }}
			>
				<div id="sidebar-scroll">
					<div
						className="sidebar-content fade-on-tour"
						style={{ height: "calc(100vh - 60px)", zIndex: "1500" }}
					>
						<div className="side-header side-content bg-white-op pl-0 pt-0">
							<img
								src={BuddyFace}
								style={{ position: "absolute", maxWidth: "85%", top: "-1rem" }}
							/>
							<div
								style={{
									display: "block",
									position: "absolute",
									top: 8,
									right: 8,
									color: "white",
									cursor: "pointer",
								}}
								onClick={toggleSidebar}
							>
								<XMarkIcon style={{ height: 16 }} />
							</div>
						</div>
						<div
							className="side-content"
							style={{ height: "calc(100vh - 60px)", paddingTop: 0 }}
						>
							<ol className="nav-main">
								<li className="nav-main-heading">
									<span className="sidebar-mini-hide">Admin</span>
								</li>
								{/* <SidebarTab
									{...{
										link: "/admin/",
										name: "Dashboard",
										icon: "HomeIcon",
									}}
								/> */}
								<SidebarTab
									{...{
										link: "/admin/organisations",
										name: "Organisation List",
										icon: "BuildingOffice2Icon",
									}}
								/>
								<SidebarTab
									{...{ link: "/admin/advisors", name: "Advisor List", icon: "ViewColumnsIcon" }}
								/>
								<SidebarTab
									{...{
										link: "/admin/payment-and-billing",
										name: "Payment & Billing",
										icon: "CreditCardIcon",
									}}
								/>
								<SidebarTab
									{...{
										link: "/admin/notifications",
										name: "Notifications",
										icon: "BellIcon",
									}}
								/>
								<li className="nav-main-heading">
									<span className="sidebar-mini-hide">Tools</span>
								</li>
								<SidebarTab
									{...{
										link: "",
										onClick: openCertificateModal,
										name: "Create Certificate",
										icon: "ClipboardDocumentCheckIcon",
									}}
								/>
								<SidebarTab
									{...{
										link: "",
										onClick: openUserModal,
										name: "Get User Information",
										icon: "MagnifyingGlassIcon",
									}}
								/>
								<li className="nav-main-heading">
									<span className="sidebar-mini-hide">Website</span>
								</li>
								<SidebarTab
									{...{
										link: "https://www.buddy.hr",
										target: "_blank",
										name: "Live",
										icon: "GlobeAltIcon",
									}}
								/>
								<SidebarTab
									{...{
										link: "https://stage.buddy.hr",
										name: "Staging",
										target: "_blank",
										icon: "WrenchIcon",
									}}
								/>
								<li className="nav-main-heading">
									<span className="sidebar-mini-hide">Payroll Portal</span>
								</li>
								<SidebarTab
									{...{
										link: "https://app.buddy.hr",
										target: "_blank",
										name: "Live",
										icon: "GlobeAltIcon",
									}}
								/>
								<SidebarTab
									{...{
										link: "https://review.app.buddy.hr",
										name: "Review",
										target: "_blank",
										icon: "ClipboardDocumentCheckIcon",
									}}
								/>
								<SidebarTab
									{...{
										link: "https://stage.app.buddy.hr",
										name: "Staging",
										target: "_blank",
										icon: "WrenchIcon",
									}}
								/>
								<li className="nav-main-heading">
									<span className="sidebar-mini-hide">Employee Portal</span>
								</li>
								<SidebarTab
									{...{
										link: "https://employee.buddy.hr",
										target: "_blank",
										name: "Live",
										icon: "GlobeAltIcon",
									}}
								/>
								<SidebarTab
									{...{
										link: "https://review.employee.buddy.hr",
										name: "Review",
										target: "_blank",
										icon: "ClipboardDocumentCheckIcon",
									}}
								/>
								<SidebarTab
									{...{
										link: "https://employee.stage.buddy.hr",
										name: "Staging",
										target: "_blank",
										icon: "WrenchIcon",
									}}
								/>
							</ol>
						</div>
					</div>
				</div>
				<div
					className="slimScrollBar"
					style={{
						background: "rgb(255, 255, 255)",
						width: "5px",
						position: "absolute",
						top: "0px",
						opacity: 0.35,
						display: "none",
						borderRadius: "7px",
						zIndex: "99",
						right: "2px",
						height: "151.918px",
					}}
				></div>
				<div
					className="slimScrollRail"
					style={{
						width: "5px",
						height: "100%",
						position: "absolute",
						top: "0px",
						display: "none",
						borderRadius: "7px",
						background: "rgb(51, 51, 51)",
						opacity: "1",
						zIndex: "90",
						right: "2px",
					}}
				></div>
			</div>
		</nav>
	);
};

const UserSidebar = ({ show, toggleSidebar, children, SidebarTab, profile: user }) => {
	if (!show) return null;

	return (
		<nav id="sidebar">
			<div
				className="slimScrollDiv"
				style={{ position: "relative" }}
			>
				<div id="sidebar-scroll">
					<div
						className="sidebar-content fade-on-tour"
						style={{ height: "calc(100vh - 60px)", zIndex: "1500" }}
					>
						<div className="side-header side-content bg-white-op pl-0 pt-0">
							<img
								src={BuddyFace}
								style={{ position: "absolute", maxWidth: "85%", top: "-1rem" }}
							/>
							<div
								style={{
									display: "block",
									position: "absolute",
									top: 8,
									right: 8,
									color: "white",
									cursor: "pointer",
								}}
								onClick={toggleSidebar}
							>
								<XMarkIcon style={{ height: 16 }} />
							</div>
						</div>
						<div
							className="side-content"
							style={{ height: "calc(100vh - 60px)", paddingTop: 0 }}
						>
							<ol className="nav-main">
								<li className="nav-main-heading">
									<span className="sidebar-mini-hide">My Portals</span>
								</li>
								{/* <SidebarTab
									{...{
										link: "https://www.buddy.hr",
										name: "My Profile",
										icon: "UserCircleIcon",
									}}
								/> */}
								<SidebarTab
									{...{
										link: "/organisation",
										name: "Company Portal",
										icon: "BuildingOffice2Icon",
									}}
								/>
								{user?.isAdvisor && (
									<SidebarTab
										{...{
											link: "/advisor",
											name: "Advisor Portal",
											icon: "ViewColumnsIcon",
										}}
									/>
								)}
								{user?.isEmployee && (
									<SidebarTab
										{...{
											link: "https://employee.buddy.hr",
											name: "Employee Portal",
											icon: "UserGroupIcon",
										}}
									/>
								)}
								<li className="nav-main-heading">
									<span className="sidebar-mini-hide">Quick Links</span>
								</li>
								{user?.isAdmin && (
									<SidebarTab
										{...{
											link: "",
											onClick: () => {
												copyToClipboard(user?.id);
											},
											name: "Copy User ID",
											icon: "ClipboardIcon",
										}}
									/>
								)}
								{user?.lastUsedOrg && (
									<SidebarTab
										{...{
											link: `/organisation?organisation=${user?.lastUsedOrg}`,
											name: user?.lastUsedOrgName,
											icon: "BuildingOfficeIcon",
										}}
									/>
								)}
								<SidebarTab
									{...{
										link: `/user/organisations`,
										name: "Company List",
										icon: "TableCellsIcon",
									}}
								/>
								{children}
							</ol>
						</div>
					</div>
				</div>
				<div
					className="slimScrollBar"
					style={{
						background: "rgb(255, 255, 255)",
						width: "5px",
						position: "absolute",
						top: "0px",
						opacity: 0.35,
						display: "none",
						borderRadius: "7px",
						zIndex: "99",
						right: "2px",
						height: "151.918px",
					}}
				></div>
				<div
					className="slimScrollRail"
					style={{
						width: "5px",
						height: "100%",
						position: "absolute",
						top: "0px",
						display: "none",
						borderRadius: "7px",
						background: "rgb(51, 51, 51)",
						opacity: "1",
						zIndex: "90",
						right: "2px",
					}}
				></div>
			</div>
		</nav>
	);
};

const CompanySidebar = ({
	companyPermissions: perms,
	show,
	toggleSidebar,
	company,
	CurrencyIcon,
	SidebarTab,
	children,
	payrollGetPayPeriod,
	navigate,
	currentPayroll,
}) => {
	const { isDemo, isSetup, jurisdiction, paySchedules, isSuspended } = company || {};
	const allow = useMemo(() => {
		if (!company?.org) return {};
		const { people, pensions, grades, payroll, taxForms, reports } = perms || {};
		const { timesheets, leave, workLocations, assets } = perms || {};
		const { settings, permissions, billing, accounting } = perms || {};

		const management = filterPermissions(
			[
				{ permit: people },
				{ permit: pensions, jurisdiction: ["UK"] },
				{ permit: grades },
				{ permit: payroll },
				{ permit: taxForms },
				{ permit: reports },
			],
			jurisdiction,
		);

		const approvals = filterPermissions(
			[
				{ permit: timesheets },
				{ permit: leave },
				{ permit: workLocations },
				{ permit: assets, jurisdiction: ["UK"] },
			],
			jurisdiction,
		);

		const admin = filterPermissions(
			[
				{ permit: settings },
				{ permit: permissions },
				{ permit: billing },
				{ permit: accounting, jurisdiction: ["UK"] },
			],
			jurisdiction,
		);

		const allowedPages = Object.entries({
			management,
			approvals,
			admin,
			people,
			pensions,
			grades,
			payroll,
			taxForms,
			reports,
			timesheets,
			leave,
			workLocations,
			assets,
			settings,
			permissions,
			billing,
			accounting,
		}).reduce((acc, [key, value]) => {
			acc[key] = hasPermissions(value);
			return acc;
		}, {});

		return allowedPages;
	}, [JSON.stringify(perms), jurisdiction, company?.org]);

	switch (jurisdiction) {
		case "UK":
			CurrencyIcon = "CurrencyPoundIcon";
			break;
		default:
			CurrencyIcon = "CurrencyEuroIcon";
	}

	if (!show || isSuspended) return null;

	return (
		<nav id="sidebar">
			<div
				className="slimScrollDiv"
				style={{ position: "relative" }}
			>
				<div id="sidebar-scroll">
					<div
						className="sidebar-content fade-on-tour"
						style={{ height: "calc(100vh - 60px)", zIndex: "1500" }}
					>
						<div className="side-header side-content bg-white-op pl-0 pt-0">
							<img
								src={BuddyFace}
								style={{ position: "absolute", maxWidth: "85%", top: "-1rem" }}
							/>
							<div
								style={{
									display: "block",
									position: "absolute",
									top: 8,
									right: 8,
									color: "white",
									cursor: "pointer",
								}}
								onClick={toggleSidebar}
							>
								<XMarkIcon style={{ height: 16 }} />
							</div>
						</div>
						<div
							className="side-content"
							style={{ height: "calc(100vh - 60px)", paddingTop: 0 }}
						>
							<ol className="nav-main">
								{/*<li>
									<Link
										to="/organisation/dashboard"
										className="nav-item"
									>
										<HomeIcon className="svg-nav-item" />
										<span>Dashboard</span>
									</Link>
								</li>*/}
								{isSetup && (
									<>
										<li className="nav-main-heading">
											<span className="sidebar-mini-hide">Setup</span>
										</li>
										<SidebarTab
											{...{
												link: "/organisation/setup",
												name: "Getting Started",
												icon: "LightBulbIcon",
											}}
										/>
									</>
								)}
								{allow?.management && (
									<li className="nav-main-heading">
										<span className="sidebar-mini-hide">Management</span>
									</li>
								)}
								{allow?.people && (
									<SidebarTab
										{...{
											link: "/organisation/people",
											name: "People",
											icon: "UserGroupIcon",
										}}
									/>
								)}
								{allow?.pensions && jurisdiction === "UK" && (
									<SidebarTab
										{...{
											link: "/organisation/pension",
											name: "Pensions",
											icon: "PiggyBankIcon",
										}}
									/>
								)}
								{allow?.grades && (
									<SidebarTab
										{...{
											link: "/organisation/grades",
											name: "Grades",
											icon: "TableCellsIcon",
										}}
									/>
								)}
								{allow?.payroll && perms?.payroll != "Read" && (
									<>
										<SidebarTab
											{...{
												link: `/organisation/payroll`,
												checkForActive: `/organisation/payroll`,
												onClick: () => {
													payrollGetPayPeriod({
														paySchedule: paySchedules?.length > 0 ? paySchedules[0].id : "",
														navigate,
													});
												},
												name: "Run Payroll",
												icon: CurrencyIcon,
												className: "open",
											}}
										>
											{paySchedules?.length > 1 && (
												<ol>
													{paySchedules
														?.filter((ps) => !ps.temporary)
														?.map((p) => (
															<SidebarTab
																{...{
																	link: `/organisation/payroll/`,
																	isActive: currentPayroll === p.id,
																	onClick: () => {
																		payrollGetPayPeriod({
																			paySchedule: p.id,
																			navigate,
																		});
																	},
																	name: p.name,
																	className: "open",
																}}
															/>
														))}
												</ol>
											)}
										</SidebarTab>
									</>
								)}
								{allow?.payroll && (
									<SidebarTab
										{...{
											link: "/organisation/payroll-history",
											name: "Payroll History",
											icon: "FolderIcon",
										}}
									/>
								)}
								{allow?.taxForms && (
									<SidebarTab
										{...{
											link: "/organisation/tax-forms",
											name: "Tax Forms",
											icon: "PencilSquareIcon",
										}}
									/>
								)}
								{allow?.reports && (
									<SidebarTab
										{...{
											link: "/organisation/reports",
											name: "Reports",
											icon: "DocumentIcon",
										}}
									/>
								)}
								{allow?.approvals && (
									<li className="nav-main-heading">
										<span className="sidebar-mini-hide">Approvals</span>
									</li>
								)}
								{allow?.assets && jurisdiction === "UK" && (
									<SidebarTab
										{...{
											link: "/organisation/assets",
											name: "Assets",
											icon: "KeyIcon",
										}}
									/>
								)}
								{allow?.timesheets && (
									<SidebarTab
										{...{
											link: "/organisation/timesheets",
											name: "Timesheets",
											icon: "ClockIcon",
										}}
									/>
								)}
								{allow?.leave && (
									<SidebarTab
										{...{
											link: "/organisation/leave",
											name: "Leave Management",
											icon: "CalendarIcon",
										}}
									/>
								)}
								{allow?.workLocations && (
									<SidebarTab
										{...{
											link: "/organisation/work-locations",
											name: "Work Locations",
											icon: "BuildingOfficeIcon",
										}}
									/>
								)}
								{allow?.admin && (
									<li className="nav-main-heading">
										<span className="sidebar-mini-hide">Admin</span>
									</li>
								)}
								{allow?.accounting && jurisdiction === "UK" && (
									<SidebarTab
										{...{
											link: "/organisation/accounting",
											name: "Accounting",
											icon: "TicketIcon",
										}}
									/>
								)}
								{allow?.settings && (
									<SidebarTab
										{...{
											link: "/organisation/settings",
											name: "Settings",
											icon: "WrenchScrewdriverIcon",
										}}
									/>
								)}
								{/* {allow?.permissions && (
									<SidebarTab
										{...{
											link: "/organisation/permissions",
											name: "Permissions",
											icon: "FingerPrintIcon",
										}}
									/>
								)} */}
								{allow?.billing && !isDemo && (
									<SidebarTab
										{...{
											link: "/organisation/billing",
											name: "Billing",
											icon: "CreditCardIcon",
										}}
									/>
								)}
								{children}
							</ol>
						</div>
					</div>
				</div>
				<div
					className="slimScrollBar"
					style={{
						background: "rgb(255, 255, 255)",
						width: "5px",
						position: "absolute",
						top: "0px",
						opacity: 0.35,
						display: "none",
						borderRadius: "7px",
						zIndex: "99",
						right: "2px",
						height: "151.918px",
					}}
				></div>
				<div
					className="slimScrollRail"
					style={{
						width: "5px",
						height: "100%",
						position: "absolute",
						top: "0px",
						display: "none",
						borderRadius: "7px",
						background: "rgb(51, 51, 51)",
						opacity: "1",
						zIndex: "90",
						right: "2px",
					}}
				></div>
			</div>
		</nav>
	);
};

const PayrollSidebar = ({
	advisorPermissions: perms,
	show,
	toggleSidebar,
	advisor,
	advisorPermStatus,
	CurrencyIcon,
	children,
	SidebarTab,
}) => {
	const { jurisdiction } = advisor || {};
	const allow = useMemo(() => {
		if (["Pending"].includes(advisorPermStatus)) return {};
		const {
			clients,
			team,
			permissions,
			payrolls,
			tasks,
			fssxpress,
			taxForms,
			everroll,
			billing,
			settings,
            reports,
			dashboard,
		} = perms || {};
		const management = filterPermissions([{ permit: clients }, { permit: team }], jurisdiction);

		const payroll = filterPermissions(
			[{ permit: payrolls }, { permit: tasks }, { permit: everroll }],
			jurisdiction,
		);

		const taxFormTab = filterPermissions(
			[
				{ permit: fssxpress, jurisdiction: ["MT"] },
				{ permit: taxForms, jurisdiction: ["UK"] },
			],
			jurisdiction,
		);

		const admin = filterPermissions(
			[{ permit: settings }, { permit: permissions }, { permit: billing }, { permit: dashboard }],
			jurisdiction,
		);

		const allowedPages = Object.entries({
			dashboard,
			clients,
			team,
			permissions,
			payrolls,
			tasks,
			payroll,
			taxForms,
			taxFormTab,
			fssxpress,
			billing,
			settings,
			management,
			admin,
			reports,
		}).reduce((acc, [key, value]) => {
			acc[key] = hasPermissions(value);
			return acc;
		}, {});

		return allowedPages;
	}, [JSON.stringify(perms), jurisdiction, advisorPermStatus]);

	switch (jurisdiction) {
		case "UK":
			CurrencyIcon = "CurrencyPoundIcon";
			break;
		default:
			CurrencyIcon = "CurrencyEuroIcon";
	}

	if (!show) return null;

	return (
		<nav
			id="sidebar"
			className="sidebar-payroll"
		>
			<div
				className="slimScrollDiv"
				style={{ position: "relative" }}
			>
				<div id="sidebar-scroll">
					<div
						className="sidebar-content fade-on-tour"
						style={{ height: "calc(100vh - 60px)", zIndex: "1500" }}
					>
						<div className="side-header side-content bg-white-op pl-0 pt-0">
							<img
								src={BuddyFace}
								style={{ position: "absolute", maxWidth: "85%", top: "-1rem" }}
							/>
							<div
								style={{
									display: "block",
									position: "absolute",
									top: 8,
									right: 8,
									color: "white",
									cursor: "pointer",
								}}
								onClick={toggleSidebar}
							>
								<XMarkIcon style={{ height: 16 }} />
							</div>
						</div>
						<div
							className="side-content"
							style={{ height: "calc(100vh - 60px)", paddingTop: 0 }}
						>
							<ol className="nav-main">
								{/* <li>
									<Link /advisor/dashboard" className="nav-item">
										<HomeIcon className="svg-nav-item" />
										<span>Dashboard</span>
									</Link>
								</li> */}
								{allow?.management && (
									<li className="nav-main-heading">
										<span className="sidebar-mini-hide">{advisor?.name}</span>
									</li>
								)}
								{allow?.dashboard && (
									<SidebarTab
										{...{
											link: "/advisor/dashboard",
											name: "Dashboard",
											icon: "ClipboardDocumentListIcon",
										}}
									/>
								)}
								{allow?.clients && (
									<SidebarTab
										{...{
											link: "/advisor/clients",
											name: "Clients",
											icon: "BuildingOffice2Icon",
										}}
									/>
								)}
								{allow?.team && (
									<SidebarTab
										{...{
											link: "/advisor/team",
											name: "My Team",
											icon: "UserGroupIcon",
										}}
									/>
								)}

								{allow?.payroll && (
									<li className="nav-main-heading">
										<span className="sidebar-mini-hide">Payroll</span>
									</li>
								)}
								{allow?.payrolls && (
									<SidebarTab
										{...{
											link: "/advisor/payrolls",
											name: "Upcoming Payrolls",
											icon: CurrencyIcon,
										}}
									/>
								)}
								{allow?.tasks && (
									<SidebarTab
										{...{ link: "/advisor/tasks", name: "Scheduled Tasks", icon: "QueueListIcon" }}
									/>
								)}

								{allow?.tasks && (
									<SidebarTab
										{...{ link: "/advisor/ever-roll", name: "EverRoll™", icon: "BoltIcon" }}
									/>
								)}

								{allow?.taxFormTab && (
									<li className="nav-main-heading">
										<span className="sidebar-mini-hide">Tax Forms</span>
									</li>
								)}
								{allow?.taxForms && (
									<SidebarTab
										{...{
											link: "/advisor/tax-forms",
											name: "Tax Forms",
											icon: "PencilSquareIcon",
										}}
									/>
								)}
								{allow?.fssxpress && jurisdiction === "MT" && (
									<SidebarTab
										{...{
											link: "/advisor/fss-xpress",
											name: "FSSXPress™",
											icon: "PencilSquareIcon",
										}}
									/>
								)}

								{allow?.admin && (
									<li className="nav-main-heading">
										<span className="sidebar-mini-hide">Admin</span>
									</li>
								)}
								{allow?.settings && (
									<SidebarTab
										{...{
											link: "/advisor/settings",
											name: "Settings",
											icon: "WrenchScrewdriverIcon",
										}}
									/>
								)}
								{/* {allow?.permissions && (
									<SidebarTab {...{ link: "/advisor/permissions", name: "Permissions", icon: "FingerPrintIcon" }} />
								)} */}
								{allow?.billing && (
									<SidebarTab
										{...{ link: "/advisor/billing", name: "Billing", icon: "CreditCardIcon" }}
									/>
								)}
								{allow?.reports && (
									<SidebarTab
										{...{
											link: "/advisor/reports",
											name: "Reports",
											icon: "DocumentIcon",
										}}
									/>
								)}
								{children}
							</ol>
						</div>
					</div>
				</div>
				<div
					className="slimScrollBar"
					style={{
						background: "rgb(255, 255, 255)",
						width: "5px",
						position: "absolute",
						top: "0px",
						opacity: 0.35,
						display: "none",
						borderRadius: "7px",
						zIndex: "99",
						right: "2px",
						height: "151.918px",
					}}
				></div>
				<div
					className="slimScrollRail"
					style={{
						width: "5px",
						height: "100%",
						position: "absolute",
						top: "0px",
						display: "none",
						borderRadius: "7px",
						background: "rgb(51, 51, 51)",
						opacity: "1",
						zIndex: "90",
						right: "2px",
					}}
				></div>
			</div>
		</nav>
	);
};

const mapStateToProps = (state) => {
	const { currentAgreement, profile: user, myAgreements } = state.my;

	const thisAgreement = myAgreements && myAgreements.find((i) => i.id == currentAgreement);

	const company = {
		...state.company?.settings,
		isSetup: state?.company?.settings?.status === "Setup",
		isSuspended: state?.company?.settings?.status === "Suspended",
	};

	const organisations = state.organisations;
	const lastUsedOrg = organisations?.find((org) => org?.id === user?.lastUsedOrg);

	const profile = {
		...user,
		lastUsedOrg: lastUsedOrg ? user.lastUsedOrg : undefined,
		lastUsedOrgName: lastUsedOrg ? lastUsedOrg?.name : undefined,
	};

	return {
		show: state.sidebar.show,
		showTimesheets:
			thisAgreement && thisAgreement.companyPortalSettings && thisAgreement.companyPortalSettings.showTimesheets,
		mode: state.mode,
		currentAgreement,
		currencyName: state.company?.settings?.currencyName,
		profile,
		company,
		advisor: state.advisors?.settings,
		advisorPermStatus: state.advisors?.permissions?.status,
		advisorPermissions: state.advisors?.permissions?.permissions,
		companyPermissions: state?.permissions?.my?.permissions,
		currentPayroll: state.payroll?.payPeriodDetails?.paySchedule,
	};
};

const mapDispatchToProps = (dispatch) => ({
	payrollGetPayPeriod: (options) => dispatch(PAYROLL_ACTIONS.payrollGetPayPeriod(options)),
	logout: () => dispatch(AUTHENTICATION_ACTIONS.logout()),
	goToZendesk: (url) => dispatch(AUTHENTICATION_ACTIONS.goToZendesk(url)),
	toggleSidebar: () => dispatch(SIDEBAR_ACTIONS.toggleSidebar()),
	openCertificateModal: () => dispatch(ADMIN_ACTIONS.getCertificateModal(true)),
	openUserModal: () => dispatch(ADMIN_ACTIONS.getUserModal(true)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
